import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Check, Clock, GripVertical, Mail, MessageSquare, BarChart3, FileText } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from '@/components/editorial/AnimatedBorderBadge';

const EditorialPlanningSection: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isVisible, setIsVisible] = useState(false);
  const [showPosts, setShowPosts] = useState<number[]>([]);
  const [showTip, setShowTip] = useState(false);
  const [dragAnimation, setDragAnimation] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const features = [
    { text: "Social Planner", icon: Calendar },
    { text: "Email Marketing Suite", icon: Mail },
    { text: "SMS & Whatsapp Marketing", icon: MessageSquare },
    { text: "Ad Campaign Management", icon: BarChart3 },
    { text: "Surveys and Forms", icon: FileText }
  ];

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const timeSlots = ['9 AM', '12 PM', '3 PM', '6 PM'];

  const scheduledPosts = [
    { day: 0, time: 1, platform: 'instagram', color: '#E4405F' },
    { day: 1, time: 2, platform: 'linkedin', color: '#0A66C2' },
    { day: 2, time: 0, platform: 'twitter', color: isDark ? '#FFFFFF' : '#000000' },
    { day: 3, time: 3, platform: 'facebook', color: '#1877F2' },
    { day: 4, time: 1, platform: 'youtube', color: '#FF0000' },
    { day: 5, time: 2, platform: 'tiktok', color: isDark ? '#FFFFFF' : '#000000' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Animate posts appearing one by one
    scheduledPosts.forEach((_, index) => {
      setTimeout(() => {
        setShowPosts(prev => [...prev, index]);
      }, 400 + index * 200);
    });

    // Show AI tip
    setTimeout(() => setShowTip(true), 2000);

    // Drag animation demo
    setTimeout(() => setDragAnimation(2), 2500);
    setTimeout(() => setDragAnimation(null), 3500);
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 px-[7%] sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge with animated border */}
            <AnimatedBorderBadge text="Schedule" icon={Calendar} align="left" />

            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Plan your month in 10 minutes.
            </h2>

            <p 
              className="text-sm mb-8"
              style={{ color: isDark ? '#AAAAAA' : '#555555' }}
            >
              AI-powered content creator, drag & drop & best posting times. Batch schedule a month of campaigns in minutes.
            </p>

            {/* Feature checklist */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${index * 100}ms`
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <span className="text-sm" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => window.location.href = '/pricing#pricing-section'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
              style={{
                backgroundColor: isDark ? '#FFFFFF' : '#000000',
                color: isDark ? '#000000' : '#FFFFFF',
              }}
            >
              Open Calendar →
            </button>
          </div>

          {/* Right Visual - Calendar UI */}
          <div>
            <div 
              className="rounded-xl overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8', 
                border: isDark ? '1px solid #333333' : '1px solid #E5E5E5',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(-4px) scale(1)' : 'translateY(20px) scale(0.95)',
                boxShadow: isVisible ? (isDark ? '0 20px 40px -10px rgba(0, 0, 0, 0.5)' : '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)') : 'none',
                transition: 'all 0.6s ease-out'
              }}
            >
              {/* Calendar header */}
              <div className="p-4 border-b" style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
                    <span className="text-sm font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>January 2026</span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      className="px-3 py-1 text-xs font-medium rounded"
                      style={{ backgroundColor: isDark ? '#FFFFFF' : '#000000', color: isDark ? '#000000' : '#FFFFFF' }}
                    >
                      Week
                    </button>
                    <button 
                      className="px-3 py-1 text-xs font-medium rounded" 
                      style={{ backgroundColor: isDark ? '#333333' : '#EEEEEE', color: isDark ? '#AAAAAA' : '#666666' }}
                    >
                      Month
                    </button>
                  </div>
                </div>
              </div>

              {/* Calendar grid */}
              <div className="p-4">
                {/* Days header */}
                <div className="grid grid-cols-7 gap-2 mb-3">
                  {days.map((day, index) => (
                    <div 
                      key={index} 
                      className="text-center"
                      style={{
                        opacity: isVisible ? 1 : 0,
                        transition: `opacity 0.3s ease-out ${index * 50}ms`
                      }}
                    >
                      <span className="text-[10px] font-medium" style={{ color: isDark ? '#666666' : '#888888' }}>{day}</span>
                    </div>
                  ))}
                </div>

                {/* Time slots grid */}
                <div className="space-y-2">
                  {timeSlots.map((time, timeIndex) => (
                    <div key={timeIndex} className="grid grid-cols-7 gap-2">
                      {days.map((_, dayIndex) => {
                        const postIndex = scheduledPosts.findIndex(p => p.day === dayIndex && p.time === timeIndex);
                        const post = postIndex !== -1 ? scheduledPosts[postIndex] : null;
                        const isShown = post && showPosts.includes(postIndex);
                        const isDragging = dragAnimation === postIndex;
                        
                        return (
                          <div 
                            key={dayIndex}
                            className="h-10 rounded-lg flex items-center justify-center relative"
                            style={{ 
                              backgroundColor: isShown ? post.color + '15' : (isDark ? '#222222' : '#FFFFFF'),
                              border: `1px solid ${isShown ? post.color + '40' : (isDark ? '#333333' : '#E5E5E5')}`,
                              transform: isDragging ? 'scale(1.1) rotate(2deg)' : 'scale(1)',
                              boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.15)' : 'none',
                              transition: 'all 0.3s ease-out',
                              zIndex: isDragging ? 10 : 1
                            }}
                          >
                            {isShown && (
                              <>
                                <GripVertical 
                                  className="w-3 h-3 absolute left-1" 
                                  style={{ 
                                    color: post.color + '60',
                                    animation: isDragging ? 'wiggle 0.3s ease-in-out infinite' : 'none'
                                  }} 
                                />
                                <div 
                                  className="w-4 h-4 rounded-full"
                                  style={{ 
                                    backgroundColor: post.color,
                                    animation: `popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`
                                  }}
                                />
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Best times indicator */}
                <div 
                  className="mt-4 p-3 rounded-lg" 
                  style={{ 
                    backgroundColor: isDark ? '#422006' : '#FEF3C7', 
                    border: isDark ? '1px solid #854d0e' : '1px solid #FCD34D',
                    opacity: showTip ? 1 : 0,
                    transform: showTip ? 'translateY(0)' : 'translateY(10px)',
                    transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Clock 
                      className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}
                      style={{ animation: showTip ? 'pulse 2s ease-in-out infinite' : 'none' }}
                    />
                    <span className={`text-xs font-medium ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>AI suggests: Post at 6 PM on weekdays for 2.3x more engagement</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default EditorialPlanningSection;
